import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useBem from '@indicia/use-bem';

import DashboardBanner from '../../../shared/components/dashboard-banner/DashboardBanner';
import StatisticsButtonType from '../../../shared/components/dashboard-banner/enums/StatisticsButtonType';
import Loader from '../../../shared/components/loader/Loader';
import RenderMode from '../../../shared/models/survey/RenderMode';
import SurveyAnswer from '../../../shared/models/survey/SurveyAnswer';
import SurveyQuestionMultipleChoice from '../../../shared/models/survey/SurveyQuestionMultipleChoice';
import SurveyQuestionType from '../../../shared/models/survey/SurveyQuestionType';
import { postAnswers } from '../../survey/data/SurveyService';
import useSurveyContext from '../../survey/store/UseSurveyContext';
import Category from '../components/category/Category';
import useResultContext from '../store/UseResultContext';
import { Result, SurveyQuestionCategory } from '../types';

const ImprovementsPage = () => {
  const { t } = useTranslation();
  const { bemClassName } = useBem('dashboard');
  const { result, updateResult, isModified, updateIsModified, lockedCategories, lockCategories, resetResults } =
    useResultContext();
  const { questionAnswers, activationCode, setQuestionAnswer, resetAnswers, survey } = useSurveyContext();

  const { echteLeeftijd, inputLeeftijd, topImprovements } = result;

  useEffect(() => {
    if (lockedCategories.improvements.length === 0 && topImprovements!.length > 0) {
      lockCategories('improvements', topImprovements!);
    }
  }, []);

  if (!survey) {
    return <Loader />;
  }

  const variableQuestions = survey.questions.filter((question) => question.variability === 'Variable');

  const categorizedQuestions: SurveyQuestionCategory[] = [];

  variableQuestions.forEach((question) => {
    const { questionType, group } = question;

    let renderMode = RenderMode.Horizontal;

    if (questionType === SurveyQuestionType.MULTIPLE_CHOICE) {
      const { renderMode: questionRenderMode } = question as SurveyQuestionMultipleChoice;
      if (questionRenderMode === RenderMode.Vertical) {
        renderMode = RenderMode.Vertical;
      }
    }

    const category = categorizedQuestions.find((c) => c.category === group);

    if (category) {
      const { renderMode: categoryRenderMode } = category;

      if (categoryRenderMode === RenderMode.Horizontal && renderMode === RenderMode.Vertical) {
        category.renderMode = RenderMode.Vertical;
      }

      category.questions.push(question);
    } else {
      categorizedQuestions.push({
        category: question.group,
        questions: [question],
        renderMode,
      });
    }
  });

  const recalculate = () => {
    postAnswers(questionAnswers, activationCode, false).then((postResult: any) => {
      const { data: postResultData } = postResult;
      updateResult(postResultData as Result);
    });
    updateIsModified?.(true);
  };

  const toggleReset = () => {
    resetAnswers();
    resetResults();
    updateIsModified(false);
  };

  const onSetNewAnswer = (questionAnswer: SurveyAnswer) => {
    setQuestionAnswer(questionAnswer);
    recalculate();
  };

  const improvementCategories = lockedCategories.improvements
    .map((categoryName) => categorizedQuestions.find((c) => c.category === categoryName))
    .filter((category): category is SurveyQuestionCategory => category !== undefined);

  return (
    <div className={bemClassName()}>
      <DashboardBanner
        inputAge={inputLeeftijd ?? 0}
        realAge={echteLeeftijd ?? 0}
        title={t('dashboard.suggestions.banner.title')}
        paragraph={t('dashboard.suggestions.banner.paragraph')}
        hasChanges={isModified}
        statisticsButtonType={StatisticsButtonType.Reset}
        onStatisticsButtonClick={toggleReset}
      />

      {improvementCategories.map((category) => (
        <Category
          key={category.category}
          category={category}
          setQuestionAnswer={onSetNewAnswer}
          showNotes
          description={t(`thema-tips.this-can-be-better.${category.category}`) as string}
        />
      ))}
    </div>
  );
};

export default ImprovementsPage;
